import { getStoredLoginIdentifier } from '../auth/sessionToken';
import {
  detectPaymentCardBrand,
  parseStoredPaymentCardBrand,
  type PaymentCardBrand,
} from './paymentCardBrand';
import { profileStorageKey } from './profileStorageKey';

const STORAGE_PREFIX_V1 = 'ofrontend.profilePaymentCard.v1';
const STORAGE_PREFIX_V2 = 'ofrontend.profilePaymentCards.v2';

export const PROFILE_PAYMENT_CARDS_MAX = 3;

export type ProfileSavedPaymentCard = {
  id: string;
  /** Last 4 digits only — never store full PAN. */
  last4: string;
  /** MM/YY */
  expiry: string;
  cardholderName: string;
  /** Israeli national ID digits when provided. */
  nationalId: string | null;
  isPrimary: boolean;
  /** Detected when the full number was entered; used for brand icons. */
  brand: PaymentCardBrand;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

function newCardId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `card-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function normalizePrimary(cards: ProfileSavedPaymentCard[]): ProfileSavedPaymentCard[] {
  if (cards.length === 0) {
    return [];
  }
  const primaryIndex = cards.findIndex((card) => card.isPrimary);
  const index = primaryIndex >= 0 ? primaryIndex : 0;
  return cards.map((card, i) => ({ ...card, isPrimary: i === index }));
}

export function parseSavedPaymentCard(raw: unknown): ProfileSavedPaymentCard | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  const last4 = typeof raw.last4 === 'string' ? digitsOnly(raw.last4).slice(-4) : '';
  const expiry = typeof raw.expiry === 'string' ? raw.expiry.trim() : '';
  const cardholderName = typeof raw.cardholderName === 'string' ? raw.cardholderName.trim() : '';
  const nationalIdRaw = typeof raw.nationalId === 'string' ? digitsOnly(raw.nationalId) : '';
  if (last4.length !== 4 || !expiry || !cardholderName) {
    return null;
  }
  const id =
    typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : newCardId();
  return {
    id,
    last4,
    expiry,
    cardholderName,
    nationalId: nationalIdRaw.length === 9 ? nationalIdRaw : null,
    isPrimary: raw.isPrimary === true,
    brand: parseStoredPaymentCardBrand(raw.brand),
  };
}

function readV1Card(loginId?: string | null): ProfileSavedPaymentCard | null {
  const raw = safeGet(profileStorageKey(STORAGE_PREFIX_V1, loginId));
  if (!raw?.trim()) {
    return null;
  }
  try {
    const parsed = parseSavedPaymentCard(JSON.parse(raw) as unknown);
    return parsed ? { ...parsed, isPrimary: true } : null;
  } catch {
    return null;
  }
}

function persistCardsList(cards: ProfileSavedPaymentCard[], loginId?: string | null): void {
  const account = loginId ?? getStoredLoginIdentifier();
  const key = profileStorageKey(STORAGE_PREFIX_V2, account);
  const normalized = normalizePrimary(cards.slice(0, PROFILE_PAYMENT_CARDS_MAX));
  if (normalized.length === 0) {
    safeRemove(key);
    return;
  }
  safeSet(key, JSON.stringify(normalized));
}

/** All saved cards for the account (max {@link PROFILE_PAYMENT_CARDS_MAX}). */
export function getStoredProfilePaymentCards(
  loginId?: string | null,
): ProfileSavedPaymentCard[] {
  const account = loginId ?? getStoredLoginIdentifier();
  const raw = safeGet(profileStorageKey(STORAGE_PREFIX_V2, account));
  if (raw?.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        const cards = parsed
          .map((item) => parseSavedPaymentCard(item))
          .filter((item): item is ProfileSavedPaymentCard => item !== null)
          .slice(0, PROFILE_PAYMENT_CARDS_MAX);
        return normalizePrimary(cards);
      }
    } catch {
      /* fall through to v1 */
    }
  }

  const legacy = readV1Card(account);
  if (!legacy) {
    return [];
  }
  const migrated = normalizePrimary([legacy]);
  persistCardsList(migrated, account);
  safeRemove(profileStorageKey(STORAGE_PREFIX_V1, account));
  return migrated;
}

/** Primary card, or first on file (legacy single-card callers). */
export function getStoredProfilePaymentCard(
  loginId?: string | null,
): ProfileSavedPaymentCard | null {
  const cards = getStoredProfilePaymentCards(loginId);
  return cards.find((card) => card.isPrimary) ?? cards[0] ?? null;
}

export function persistProfilePaymentCards(
  cards: ProfileSavedPaymentCard[],
  loginId?: string | null,
): void {
  persistCardsList(cards, loginId);
  const account = loginId ?? getStoredLoginIdentifier();
  safeRemove(profileStorageKey(STORAGE_PREFIX_V1, account));
}

/** @deprecated Prefer {@link persistProfilePaymentCards}. Replaces the whole list with one card. */
export function persistProfilePaymentCard(
  card: ProfileSavedPaymentCard,
  loginId?: string | null,
): void {
  persistProfilePaymentCards(
    [{ ...card, id: card.id || newCardId(), isPrimary: true }],
    loginId,
  );
}

export function clearStoredProfilePaymentCard(loginId?: string | null): void {
  const account = loginId ?? getStoredLoginIdentifier();
  safeRemove(profileStorageKey(STORAGE_PREFIX_V2, account));
  safeRemove(profileStorageKey(STORAGE_PREFIX_V1, account));
}

export function formatCardNumberInput(value: string): string {
  return digitsOnly(value)
    .slice(0, 19)
    .replace(/(\d{4})(?=\d)/g, '$1 ')
    .trim();
}

export function formatCardExpiryInput(value: string): string {
  const digits = digitsOnly(value).slice(0, 4);
  if (digits.length <= 2) {
    return digits;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

export function formatNationalIdInput(value: string): string {
  return digitsOnly(value).slice(0, 9);
}

export function formatCvcInput(value: string): string {
  return digitsOnly(value).slice(0, 4);
}

export function formatPaymentCardLast4Display(last4: string): string {
  const digits = digitsOnly(last4).slice(-4);
  if (digits.length !== 4) {
    return '••••';
  }
  return `•••• •••• •••• ${digits}`;
}

export function formatProfilePaymentCardSummaryLine(card: ProfileSavedPaymentCard): string {
  return `${formatPaymentCardLast4Display(card.last4)} · ${card.expiry} · ${card.cardholderName}`;
}

/** Build a storable card summary after successful validation (drops PAN + CVC). */
export function paymentCardSummaryFromValidatedInput(input: {
  cardNumber: string;
  expiry: string;
  cardholderName: string;
  nationalId?: string;
  isPrimary?: boolean;
  id?: string;
}): ProfileSavedPaymentCard {
  const last4 = digitsOnly(input.cardNumber).slice(-4);
  const nationalId = formatNationalIdInput(input.nationalId ?? '');
  return {
    id: input.id?.trim() || newCardId(),
    last4,
    expiry: formatCardExpiryInput(input.expiry),
    cardholderName: input.cardholderName.trim(),
    nationalId: nationalId.length === 9 ? nationalId : null,
    isPrimary: input.isPrimary === true,
    brand: detectPaymentCardBrand(input.cardNumber),
  };
}

export function setProfilePaymentCardPrimary(
  cards: ProfileSavedPaymentCard[],
  cardId: string,
): ProfileSavedPaymentCard[] {
  if (!cards.some((card) => card.id === cardId)) {
    return normalizePrimary(cards);
  }
  return cards.map((card) => ({ ...card, isPrimary: card.id === cardId }));
}
